import { motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { CoverImage, MainContent, SideBarContent } from './components';

import { Button, Divider, Loader } from '@/components';
import { Layout } from '@/components/GenericsTournaments';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMetaData } from '@/hooks/useMetaData';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  getFullInitMl,
  getInitMl,
  getListParticipatingMl,
  getListPendingMl,
} from '@/services/minileagues';
import { Maybe } from '@/types';
import {
  EnumCoverMinileagues,
  MiniLigasTO,
  ResponseInitMiniligas,
} from '@/types/minileagues';
import { FADE_IN_OUT, scrollSmooth, TEXTS } from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

interface State {
  init: Maybe<ResponseInitMiniligas>;
  recommendedList: MiniLigasTO[] | null;
  playingList: MiniLigasTO[] | null;
  pendingList: MiniLigasTO[] | null;
  previousList: MiniLigasTO[] | null;
  invitationList: MiniLigasTO[] | null;
  mounted: boolean;
}

const initialState: State = {
  init: null,
  recommendedList: null,
  playingList: null,
  pendingList: null,
  previousList: null,
  invitationList: null,
  mounted: false,
};

const MLpage = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isMobile, isDesktop } = useIsDesktop();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState<State>(initialState);
  const { tourneyData, updateTourney } = useTourney();
  const location = useLocation();
  // 10 minutos despues de la veda JOSEFINA corre un proceso que autoasigna a las miniligas los ususarios que no estan participando (y que tienen equipo11)
  const [
    isParticipationWaitingForAutomaticMl,
    setIsParticipationWaitingForAutomaticMl,
  ] = useState(false);
  const [isMiniLeaguesInProgress, setIsMiniLeaguesInProgress] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  useMetaData({
    title: 'Miniligas',
  });

  const {
    data,
    isLoading,
    isRefetching,
    refetch: refetchAll,
  } = useQuery('getFullInitMl', async () => await getFullInitMl(), {
    refetchOnWindowFocus: false,
    keepPreviousData: false,
  });
  const { userInitialData } = useAuth();
  const handleButtonCreate = () => {
    if (userInitialData?.dtExtranjero) {
      return toast.show(TEXTS.exclusiveForArgentinianDTs);
    }
    navigate(ROUTES.minileagues.create);
  };
  useEffect(() => {
    if (
      data?.init?.estado &&
      data?.listParticipatingMl.estado &&
      data?.listPreviousMl.estado &&
      data?.listSuggestedUsersMl.estado &&
      !isLoading &&
      !isRefetching
    ) {
      const {
        init,
        listPendingMl,
        listParticipatingMl,
        listPreviousMl,
        listSuggestedUsersMl,
      } = data;

      updateTourney({
        tourney: init,
        tourneyStarted: init?.comenzaron,
        tourneysPlaying: listParticipatingMl?.misMiniLigas || [],
        tourneysPending:
          (listPendingMl.estado && listPendingMl.misMiniLigasPostuladas) || [],
        maxNumParticipants: init?.cantPartMaxXML,
      });

      setIsDataReady(true);
      setState((prevState) => ({
        ...prevState,
        init,
        recommendedList: listSuggestedUsersMl.miniLigasSugeridas || [],
        invitationList: listPendingMl.estado
          ? listPendingMl.misMiniLigas || null
          : null,
        previousList: listPreviousMl.miniLigasAnt || null,
        playingList: listParticipatingMl.misMiniLigas || null,
        pendingList: listPendingMl.estado
          ? listPendingMl.misMiniLigasPostuladas || null
          : null,

        mounted: true,
      }));

      setIsParticipationWaitingForAutomaticMl(
        Boolean(
          init?.comenzaron === true &&
            init?.participa === false &&
            init.ordenPrimeraFechaML !== undefined &&
            (init.ordenFechaPrimerEquipoUsuario ?? 1000) <=
              init.ordenPrimeraFechaML
        )
      );

      setIsMiniLeaguesInProgress(Boolean(init?.comenzaron && !init.participa));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isRefetching, refetchAll]);

  useEffect(() => {
    // handleResetTourney();
    return () => {
      setState(initialState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresco pendientes de aceptacion/participando al invitar postulacion
  const onHandleAfterInvitation = useCallback(
    async (pendings = false) => {
      const [init, listPendingMl, listPlaying] = await Promise.all([
        getInitMl(),
        getListPendingMl(),
        getListParticipatingMl(),
      ]);
      if (listPendingMl?.estado) {
        if (pendings) {
          setState((prevState) => ({
            ...prevState,
            init,
            invitationList: listPendingMl.misMiniLigas,
            pendingList: listPendingMl.misMiniLigasPostuladas,
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            init,
            invitationList: listPendingMl.misMiniLigas,
            pendingList: listPendingMl.misMiniLigasPostuladas,
            playingList: listPlaying.misMiniLigas,
          }));
        }
        updateTourney({
          tourney: init,
          tourneysInvitations: listPendingMl.misMiniLigas,
          tourneysPending: listPendingMl.misMiniLigasPostuladas,
          tourneysPlaying: listPlaying.misMiniLigas,
        });
      } else {
        toast.show(TEXTS.defaultError);
      }
    },
    [updateTourney, toast]
  );

  // effecto escucha al enviar postulacion y refrezca pendientes
  useEffect(() => {
    if (tourneyData.idsPendings.length > 0 && state.mounted) {
      onHandleAfterInvitation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPendings]);

  // effecto escucha al enviar postulacion y refrezca participando
  useEffect(() => {
    if (tourneyData.idsPlayings.length > 0 && state.mounted) {
      onHandleAfterInvitation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourneyData.idsPlayings]);
  scrollSmooth();

  useEffect(() => {
    if (!location.state?.comesFromFinal && state.init?.confDesenlace) {
      return navigate(ROUTES.minileagues.viewFinal);
    }
  }, [state.init?.confDesenlace]);

  return (
    <>
      <Layout
        adsSections={SECTION_NAME_FOR_ADS.miniligas}
        backButton={
          isDesktop &&
          location.state?.comesFromFinal &&
          state.init?.confDesenlace
        }
        backButtonAction={() => {
          navigate(ROUTES.minileagues.viewFinal);
        }}
        className="px-4 pt-3 ml:pt-0 lg:pt-0"
        coverTop={() =>
          isMobile &&
          !location.state?.comesFromFinal &&
          !state.init?.confDesenlace ? (
            <CoverImage
              context={
                isMiniLeaguesInProgress
                  ? EnumCoverMinileagues.InProgress
                  : isParticipationWaitingForAutomaticMl
                  ? EnumCoverMinileagues.NoParticipate
                  : EnumCoverMinileagues.Prize
              }
            />
          ) : null
        }
        footerContent={() => (
          <div className="flex flex-col items-center justify-center lg:hidden">
            {!isParticipationWaitingForAutomaticMl &&
              !isMiniLeaguesInProgress &&
              !tourneyData.tourneyStarted && (
                <Button
                  className="mt-4 w-full text-center font-bold"
                  disabled={tourneyData.tourneyStarted}
                  type="button"
                  variant="primary"
                  onClick={handleButtonCreate}
                >
                  Crear Miniliga
                </Button>
              )}
            <Button
              className="mt-4 mx-auto font-bold"
              style={{ textAlign: 'center' }}
              type="button"
              variant="text"
              onClick={() => {
                isMobile
                  ? navigate(ROUTES.minileagues.info)
                  : updateTourney({ showModalInfo: true });
              }}
            >
              Más información y Premios
            </Button>
          </div>
        )}
        main={() => (
          <div className="lg:pr-[111px] lg:min-h-[600px]">
            {!isDataReady ? (
              <Loader isVisible={true} />
            ) : (
              <MainContent
                initData={state.init}
                invitationList={state.invitationList}
                isMiniLeaguesInProgress={isMiniLeaguesInProgress}
                isMounted={state.mounted}
                isParticipationWaitingForAutomaticMl={
                  isParticipationWaitingForAutomaticMl
                }
                pendingList={state.pendingList}
                playingList={state.playingList}
                previousList={state.previousList}
                refetchFather={(fullReload) => {
                  if (fullReload) {
                    refetchAll();
                  } else {
                    onHandleAfterInvitation();
                  }
                }}
              />
            )}
          </div>
        )}
        mobile={{
          leftContent: {
            ...(location.state?.comesFromFinal && state.init?.confDesenlace
              ? {
                  backAction: () => {
                    navigate(ROUTES.minileagues.viewFinal);
                  },
                }
              : { showMenuAction: true, items: true }),
          },
          centerContent: { title: 'Miniligas' },
        }}
        sidebar={() => {
          if (tourneyData.tourneyStarted) {
            return <div className="lg:w-[334px]" />;
          }
          if (isParticipationWaitingForAutomaticMl || isMiniLeaguesInProgress) {
            return null;
          }
          return (
            <motion.div
              animate={'show'}
              className="lg:min-h-[700px]"
              initial="hide"
              variants={FADE_IN_OUT}
            >
              <div className="lg:w-[334px]">
                {Boolean(
                  state.playingList?.length || state.invitationList?.length
                ) && (
                  <Divider
                    className="mt-6 mb-7 h-[2px] lg:hidden"
                    type="horizontal"
                  />
                )}
                <SideBarContent list={state.recommendedList || []} />
              </div>
            </motion.div>
          );
        }}
      />
    </>
  );
};

export default MLpage;
